/**
 * Creating a reward: the mint policy.
 *
 * Three independent choices, each made explicitly:
 *
 *   POOL     which collectibles — named ids, or everything carrying a tag
 *   RARITY   where a copy's tier comes from, if it has one
 *   SUPPLY   ONE axis. Not several with a precedence rule.
 *
 * The single most important thing on this screen is the COMPUTED TOTAL at the
 * bottom, shown live. Their editor's own comment, which is the whole reason it
 * is here: *"'Common 100' across a 14-card pool means 1,400 commons, not 100 —
 * getting that backwards is a two-orders-of-magnitude mistake in how much of a
 * season exists."* A print sheet multiplies by the pool and by the tiers, and
 * nobody does that arithmetic reliably in their head.
 */
import { useMemo, useState, type ReactNode } from 'react';
import type { Item, RarityTier, RewardInput } from '@tomo/contracts';
import { Shell } from '../components/Shell.jsx';
import { Badge, Button, Failed, Spinner } from '../components/primitives.jsx';
import { Check, Choice, Field, Lines, Num, Text } from '../components/form.jsx';
import { items as itemsApi, rarity as rarityApi, rewards } from '../lib/tomo.js';
import { ApiError } from '../lib/api.js';
import { useAsync } from '../useAsync.js';
import { navigate, hrefFor } from '../lib/router.js';

type PoolMode = 'items' | 'tag';
type RarityMode = 'none' | 'rolled' | 'per_item';
type Axis = 'unlimited' | 'total' | 'per_item' | 'per_item_tier';

export function NewReward({ channelId }: { channelId: string }): ReactNode {
  const [loaded] = useAsync(
    async (signal) => ({
      tiers: await rarityApi.list(channelId, signal),
      items: await itemsApi.list(channelId, signal),
    }),
    [channelId]
  );

  if (loaded.state === 'loading') {
    return <Shell channelId={channelId}><Spinner label="Loading tiers and items…" /></Shell>;
  }
  if (loaded.state === 'failed') {
    return (
      <Shell channelId={channelId}>
        <Failed message="Could not load this channel’s tiers and items." detail={loaded.error.message} />
      </Shell>
    );
  }
  return <Form channelId={channelId} tiers={loaded.value.tiers} items={loaded.value.items} />;
}

function Form(
  { channelId, tiers, items }: { channelId: string; tiers: RarityTier[]; items: Item[] }
): ReactNode {
  const [name, setName] = useState('');

  const [poolMode, setPoolMode] = useState<PoolMode>('tag');
  const [itemIds, setItemIds] = useState<string[]>([]);
  const [tag, setTag] = useState('');

  const [rarityMode, setRarityMode] = useState<RarityMode>('none');
  const [weights, setWeights] = useState<Record<string, number>>({});

  const [axis, setAxis] = useState<Axis>('total');
  const [limit, setLimit] = useState(50);
  const [limits, setLimits] = useState<Record<string, number>>({});

  const [editions, setEditions] = useState<'random' | 'sequential'>('random');
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const live = tiers.filter((t) => !t.retired);
  const allTags = useMemo(() => [...new Set(items.flatMap((i) => i.tags))].sort(), [items]);

  /**
   * How many items this pool resolves to RIGHT NOW.
   *
   * A tag pool is a live query, so this is a snapshot rather than a promise —
   * but it is the number the total below is computed from, and showing the
   * total without it would be showing arithmetic with a hidden operand.
   */
  const poolSize = poolMode === 'items'
    ? itemIds.length
    : (tag === '' ? 0 : items.filter((i) => i.tags.includes(tag)).length);

  const total = capacityOf(axis, limit, limits, poolSize);

  const problems: string[] = [];
  if (poolMode === 'items' && itemIds.length === 0) problems.push('Name at least one item, or draw on a tag instead.');
  if (poolMode === 'tag' && tag === '') problems.push('Pick a tag for this reward to draw from.');
  if (poolMode === 'tag' && tag !== '' && poolSize === 0) {
    problems.push(`No active item carries "${tag}", so this reward would mint nothing.`);
  }
  if (axis === 'per_item_tier' && rarityMode === 'none') {
    problems.push('A per-tier print run needs a rarity mode. Roll them, or assign one per item.');
  }
  if (axis === 'per_item_tier' && rarityMode === 'rolled' && Object.keys(weights).length > 0) {
    problems.push('A per-tier print run already sets the odds. Remove the weights, or change the axis.');
  }
  if (axis !== 'per_item_tier' && rarityMode === 'rolled' && !Object.values(weights).some((n) => n > 0)) {
    problems.push('Rolled rarity needs a weight for at least one tier.');
  }
  if (axis === 'per_item_tier' && !Object.values(limits).some((n) => n > 0)) {
    problems.push('Every tier is set to zero, so this reward could never mint anything.');
  }

  const fields = error?.byField() ?? {};
  const ready = name.length > 0 && problems.length === 0;

  const submit = (): void => {
    setBusy(true);
    setError(null);
    const input: RewardInput = {
      name,
      pool: poolMode === 'items' ? { mode: 'items', item_ids: itemIds } : { mode: 'tag', tag },
      rarity: rarityMode === 'rolled'
        ? { mode: 'rolled', weights: positive(weights) }
        : rarityMode === 'per_item'
          ? { mode: 'per_item', assign: {} }
          : { mode: 'none' },
      supply: supplyValue(axis, limit, limits),
      editions,
      active,
    };
    rewards.create(channelId, input).then(
      ({ id }) => { navigate(hrefFor([channelId, 'rewards', id])); },
      (err: unknown) => {
        setBusy(false);
        setError(err instanceof ApiError
          ? err
          : new ApiError('malformed_response', 0, 'Something unexpected went wrong.'));
      }
    );
  };

  return (
    <Shell channelId={channelId}>
      <h1 className="text-xl font-semibold tracking-tight">New reward</h1>
      <p className="mt-1 text-sm text-ink-400">
        A mint policy: which collectibles, at what rarity, how many.
      </p>

      {error ? <div className="mt-6"><Failed message={error.message} /></div> : null}

      <form
        className="mt-8 max-w-2xl space-y-6"
        onSubmit={(e) => { e.preventDefault(); submit(); }}
      >
        <Field label="Name" error={fields.name}>
          <Text value={name} onChange={setName} placeholder="First Light" maxLength={120} />
        </Field>

        <Field
          label="What it can mint"
          hint={poolMode === 'tag'
            ? 'Every active item carrying this tag, resolved at mint. Adding an item later adds capacity — it never changes what anything else has left.'
            : 'Exactly these items, named.'}
        >
          <Choice
            value={poolMode}
            onChange={(mode) => { setPoolMode(mode); }}
            options={[
              { value: 'tag', label: 'Everything with a tag' },
              { value: 'items', label: 'Named items' },
            ]}
          />
        </Field>

        {poolMode === 'tag' ? (
          <Field label="Tag" error={fields['pool.tag']}>
            <Choice
              value={tag}
              onChange={setTag}
              options={[
                { value: '', label: 'Pick a tag…' },
                ...allTags.map((t) => ({ value: t, label: `#${t}` })),
              ]}
            />
          </Field>
        ) : (
          <Field label="Items" hint="One id per line." error={fields['pool.item_ids']}>
            <Lines value={itemIds} onChange={setItemIds} placeholder="itm_…" />
          </Field>
        )}

        <Field
          label="Rarity"
          hint={rarityMode === 'none'
            ? 'Copies carry no tier.'
            : rarityMode === 'rolled'
              ? 'Rolled per copy — any item can come out at any tier.'
              : 'Fixed per item. The Legendary Sword is always legendary.'}
        >
          <Choice
            value={rarityMode}
            onChange={(mode) => { setRarityMode(mode); }}
            options={[
              { value: 'none', label: 'No tiers' },
              { value: 'rolled', label: 'Rolled per copy' },
              { value: 'per_item', label: 'Fixed per item' },
            ]}
          />
        </Field>

        {rarityMode === 'rolled' && axis !== 'per_item_tier' ? (
          <Field
            label="Tier weights"
            hint="Relative, not percentages — 3 and 1 means the same as 75 and 25."
            error={fields['rarity.weights']}
          >
            <TierNumbers tiers={live} values={weights} onChange={setWeights} />
          </Field>
        ) : null}

        <Field
          label="Supply"
          hint="ONE axis, chosen here. There is no precedence rule because there is nothing to resolve."
          error={fields.supply}
        >
          <Choice
            value={axis}
            onChange={(next) => { setAxis(next); }}
            options={[
              { value: 'unlimited', label: 'Unlimited — never runs out' },
              { value: 'total', label: 'N copies in total' },
              { value: 'per_item', label: 'N copies of each item' },
              { value: 'per_item_tier', label: 'A print sheet — N of each item at each tier' },
            ]}
          />
        </Field>

        {axis === 'total' || axis === 'per_item' ? (
          <Field
            label={axis === 'total' ? 'Copies in total' : 'Copies of each item'}
            error={fields['supply.limit']}
          >
            <Num value={limit} onChange={setLimit} min={1} max={axis === 'total' ? 100000 : 10000} />
          </Field>
        ) : null}

        {axis === 'per_item_tier' ? (
          <Field
            label="Copies of each item, per tier"
            hint="The sheet IS the odds — printing 100 commons to 5 legendaries already says the distribution."
            error={fields['supply.limits']}
          >
            <TierNumbers tiers={live} values={limits} onChange={setLimits} />
          </Field>
        ) : null}

        {axis !== 'unlimited' ? (
          <Field
            label="Edition numbers"
            hint={editions === 'random'
              ? 'Out of order, so #17 of 25 can be the first ever minted and nobody can infer how early they were.'
              : 'In order — for a deliberate "first hundred".'}
          >
            <Choice
              value={editions}
              onChange={setEditions}
              options={[
                { value: 'random', label: 'Random' },
                { value: 'sequential', label: 'Sequential' },
              ]}
            />
          </Field>
        ) : null}

        <Check checked={active} onChange={setActive} label="Active" />

        <Total axis={axis} poolSize={poolSize} total={total} limit={limit} limits={limits} tiers={live} />

        {problems.map((p) => (
          <p key={p} role="alert" className="rounded-md border border-warn-400/40 bg-warn-400/5 p-3 text-xs text-warn-400">
            {p}
          </p>
        ))}

        <div className="flex gap-3">
          <Button type="submit" disabled={busy || !ready}>
            {busy ? 'Creating…' : 'Create reward'}
          </Button>
          <Button variant="ghost" onClick={() => { navigate(hrefFor([channelId, 'rewards'])); }}>
            Cancel
          </Button>
        </div>
      </form>
    </Shell>
  );
}

/** One number per live tier, keyed by the tier KEY and labelled by its label. */
function TierNumbers(
  { tiers, values, onChange }: {
    tiers: RarityTier[];
    values: Record<string, number>;
    onChange: (next: Record<string, number>) => void;
  }
): ReactNode {
  if (tiers.length === 0) {
    return (
      <p className="text-xs text-warn-400">
        This channel has no rarity tiers yet. Define some first.
      </p>
    );
  }
  return (
    <ul className="space-y-2">
      {tiers.map((tier) => (
        <li key={tier.key} className="flex items-center gap-3">
          <span
            aria-hidden
            className="h-3 w-3 shrink-0 rounded-full"
            style={{ backgroundColor: tier.color }}
          />
          <span className="flex-1 text-sm">{tier.label}</span>
          <span className="w-28">
            <Num
              value={values[tier.key] ?? 0}
              onChange={(n) => { onChange({ ...values, [tier.key]: n }); }}
              min={0}
              max={10000}
            />
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The arithmetic, spelled out rather than just totalled.
 *
 * Showing `2,000` alone would be a number to take on trust. Showing
 * `4 tiers × 20 items × 25 = 2,000` is a number somebody can disagree with,
 * which is the only kind that catches a two-orders-of-magnitude mistake.
 */
function Total(
  { axis, poolSize, total, limit, limits, tiers }: {
    axis: Axis; poolSize: number; total: number | null;
    limit: number; limits: Record<string, number>; tiers: RarityTier[];
  }
): ReactNode {
  if (axis === 'unlimited') {
    return (
      <p className="rounded-md border border-ink-800 p-3 text-sm text-ink-400">
        Unlimited. Copies carry no edition number, because there is no denominator to promise.
      </p>
    );
  }

  const perItem = axis === 'per_item_tier'
    ? tiers.reduce((n, t) => n + Math.max(0, limits[t.key] ?? 0), 0)
    : limit;

  return (
    <div className="rounded-md border border-ink-800 p-3">
      <div className="flex items-baseline gap-2">
        <span className="text-sm text-ink-400">This reward can ever mint</span>
        <Badge tone="brand">{total === null ? '∞' : total.toLocaleString()}</Badge>
        <span className="text-sm text-ink-400">copies.</span>
      </div>
      <p className="mt-1 font-mono text-xs text-ink-400">
        {axis === 'total'
          ? `${String(limit)} in total, whatever the pool does`
          : `${String(poolSize)} item${poolSize === 1 ? '' : 's'} × ${String(perItem)} each = ${String(total ?? 0)}`}
      </p>
      {axis === 'per_item_tier' ? (
        <p className="mt-1 text-xs text-ink-400">
          Per tier, across the whole pool:{' '}
          {tiers
            .filter((t) => (limits[t.key] ?? 0) > 0)
            .map((t) => `${t.label} ${String((limits[t.key] ?? 0) * poolSize)}`)
            .join(' · ') || '—'}
        </p>
      ) : null}
    </div>
  );
}

/** Total copies across the axis. null = unlimited. */
function capacityOf(
  axis: Axis, limit: number, limits: Record<string, number>, poolSize: number
): number | null {
  switch (axis) {
    case 'unlimited': return null;
    case 'total': return limit;
    case 'per_item': return limit * poolSize;
    case 'per_item_tier':
      return Object.values(limits).reduce((n, v) => n + Math.max(0, v), 0) * poolSize;
  }
}

function supplyValue(
  axis: Axis, limit: number, limits: Record<string, number>
): RewardInput['supply'] {
  switch (axis) {
    case 'unlimited': return { axis: 'unlimited' };
    case 'total': return { axis: 'total', limit };
    case 'per_item': return { axis: 'per_item', limit };
    case 'per_item_tier': return { axis: 'per_item_tier', limits: positive(limits) };
  }
}

/** Drop the zeroes a form leaves behind, so an untouched tier is simply absent. */
const positive = (m: Record<string, number>): Record<string, number> =>
  Object.fromEntries(Object.entries(m).filter(([, n]) => n > 0));
